// Module: MultiImageUploadBrick
// Role: Multi-image checkpoint upload: presign/upload each required image, submit once.
// Uses: lib/uploadTelemetry:createUploadFunnel+uploadTimeoutFor,
//       lib/imageResize:ImageResizeError,
//       screens/pantene/_uploadXHR:UploadTimeoutError
// Used by: brick-registry.ts for checkpoints with multiple image artefact requirements.
// FTR-1317: funnel telemetry replaces ad-hoc apiFetch beacons.

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { PlatformContext } from '../../../providers/PlatformProvider';
import { useFileUpload } from '../../../hooks/useFileUpload';
import { useArtefactPresign, useSubmitCheckpoint } from '../../../api/flow-client';
import { MAX_PHOTO_INPUT_BYTES, FLOW_PHOTO_MAX_DIM } from '../../../shared/limits';
import { resizeImageToBlob, ImageResizeError } from '../../../lib/imageResize';
import { createUploadFunnel, uploadTimeoutFor } from '../../../lib/uploadTelemetry';
import { UploadTimeoutError } from '../../../screens/pantene/_uploadXHR';
import { submitErrorText } from '../submitErrorText';
import { Frame } from '../atoms/Frame';
import { MiniHeader } from '../atoms/MiniHeader';
import { InstructionCard } from '../atoms/InstructionCard';
import { WizardCtaBar } from '../../../components/atoms/WizardCtaBar';
import { HintBar } from '../atoms/HintBar';
import { StepStrip } from '../StepStrip';
import { Ic } from '../../../components/atoms/Ic';
import { P, S, T } from '../../../styles/tokens';
import { flowStepLabel } from '../_labels';
import type {
  ArtefactRequirement,
  Checkpoint,
  CheckpointAttempt,
  SlotFlowRun,
} from '../../../api/flow-types';

const IMAGE_KINDS = new Set(['screenshot', 'real_photo']);
const SUCCESS_SOFT_BG = '#EFF7E9';

interface UploadedImage {
  artefactPath: string;
  thumbUrl: string;
}

export interface MultiImageUploadBrickProps {
  checkpoint: Checkpoint;
  attempt?: CheckpointAttempt;
  flowRun: SlotFlowRun;
  checkpoints: Checkpoint[];
  attempts: CheckpointAttempt[];
  onCta: () => void;
}

function imageRequirements(checkpoint: Checkpoint): ArtefactRequirement[] {
  return checkpoint.artefact_requirements.filter((req) => IMAGE_KINDS.has(req.kind));
}

function roleFor(req: ArtefactRequirement, index: number): string {
  return req.role ?? `image_${index + 1}`;
}

function labelFor(req: ArtefactRequirement, index: number): string {
  if (req.label) return req.label;
  if (req.role === 'pvz_status_screenshot') return 'Скриншот статуса получения';
  if (req.role === 'received_product_photo') return 'Фото полученного товара';
  if (req.role === 'damaged_barcode_photo') return 'Фото поврежденного ШК';
  return `Фото ${index + 1}`;
}

function descriptionFor(req: ArtefactRequirement): string {
  if (req.description) return req.description;
  if (req.kind === 'screenshot') return 'Скриншот из приложения маркетплейса.';
  return 'Фото товара или упаковки.';
}

export function MultiImageUploadBrick({
  checkpoint,
  flowRun,
  checkpoints,
}: MultiImageUploadBrickProps) {
  const platform = useContext(PlatformContext);
  const canUpload = platform?.capabilities.fileUpload ?? false;

  const requirements = useMemo(() => imageRequirements(checkpoint), [checkpoint]);
  const [hintMessage, setHintMessage] = useState<string | null>(null);
  const [uploaded, setUploaded] = useState<Record<number, UploadedImage>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  // Preview shown immediately on pick, before presign+upload finish for that slot.
  const [pendingPreview, setPendingPreview] = useState<{ index: number; url: string } | null>(null);
  const uploadedRef = useRef<Record<number, UploadedImage>>({});
  const pendingPreviewRef = useRef<string | null>(null);
  const activeIndexRef = useRef(0);

  const upload = useFileUpload();
  const presign = useArtefactPresign(flowRun.slot_id);
  const submitCheckpoint = useSubmitCheckpoint(flowRun.slot_id, checkpoint.id);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const parentCount = checkpoints.filter((c) => c.parent_checkpoint_id === null).length;
  const uploadedCount = requirements.filter((_, index) => Boolean(uploaded[index])).length;
  const isComplete = requirements.length > 0 && uploadedCount === requirements.length;
  const isUploading = upload.status === 'uploading';
  const isSubmitting = submitCheckpoint.isPending;

  useEffect(() => {
    uploadedRef.current = uploaded;
  }, [uploaded]);

  useEffect(
    () => () => {
      Object.values(uploadedRef.current).forEach((item) => URL.revokeObjectURL(item.thumbUrl));
      if (pendingPreviewRef.current !== null) URL.revokeObjectURL(pendingPreviewRef.current);
    },
    [],
  );

  const pickSlot = useCallback(
    (index: number) => {
      if (!canUpload) {
        setHintMessage('Загрузка файлов недоступна на этой платформе.');
        return;
      }
      if (isSubmitted || isUploading || isSubmitting) return;
      activeIndexRef.current = index;
      fileInputRef.current?.click();
    },
    [canUpload, isSubmitted, isUploading, isSubmitting],
  );

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (!file) return;

      const currentIndex = activeIndexRef.current;
      const req = requirements[currentIndex];
      if (!req) return;

      // BUG-1304: 50 MB input guard
      const maxSize = req.max_size_bytes ?? MAX_PHOTO_INPUT_BYTES;
      if (file.size > maxSize) {
        setHintMessage('Файл слишком большой. Уменьши и попробуй снова.');
        return;
      }

      setHintMessage(null);

      // FTR-1317: upload funnel telemetry accumulator
      const funnel = createUploadFunnel(flowRun.slot_id, checkpoint.slug);
      funnel.stage('file_picked', { mime: file.type, size: file.size });

      // BUG-1304: normalize to JPEG
      let uploadFile = file;
      try {
        funnel.stage('resize_start');
        const blob = await resizeImageToBlob(file, { maxDim: FLOW_PHOTO_MAX_DIM, quality: 0.85 });
        uploadFile = new File([blob], 'upload.jpg', { type: 'image/jpeg' });
        funnel.stage('resize_done', { size: blob.size });
      } catch (resizeErr) {
        if (resizeErr instanceof ImageResizeError && resizeErr.code === 'decode_timeout') {
          funnel.stage('resize_timeout');
          funnel.flush('timeout');
          setHintMessage('Загрузка идёт дольше обычного — проверь интернет и попробуй ещё раз');
          return;
        }
        funnel.stage('resize_failed', { code: resizeErr instanceof ImageResizeError ? resizeErr.code : undefined });
        console.warn('[FTR-1317] re-encode failed, uploading original:', resizeErr);
      }

      const objectUrl = URL.createObjectURL(uploadFile);
      if (pendingPreviewRef.current !== null) URL.revokeObjectURL(pendingPreviewRef.current);
      pendingPreviewRef.current = objectUrl;
      setPendingPreview({ index: currentIndex, url: objectUrl });

      try {
        funnel.stage('presign_start');
        const presignResult = await presign.mutateAsync({
          checkpoint_slug: checkpoint.slug,
          artefact_kind: req.kind,
          content_md5: '',
          file_size_bytes: uploadFile.size,
        });
        funnel.stage('presign_done');

        funnel.stage('upload_start');
        await upload.start({ url: presignResult.upload_url, file: uploadFile, timeoutMs: uploadTimeoutFor(uploadFile.size) });
        funnel.stage('upload_done');

        funnel.flush('success');
        pendingPreviewRef.current = null;
        setUploaded((prev) => {
          const old = prev[currentIndex]?.thumbUrl;
          if (old) URL.revokeObjectURL(old);
          return {
            ...prev,
            [currentIndex]: {
              artefactPath: presignResult.artefact_path,
              thumbUrl: objectUrl,
            },
          };
        });
        setPendingPreview((p) => (p?.index === currentIndex ? null : p));
      } catch (err: unknown) {
        const isTimeout = err instanceof UploadTimeoutError || (err instanceof DOMException && err.name === 'AbortError');
        if (isTimeout) {
          funnel.stage('upload_timeout');
          funnel.flush('timeout');
          setHintMessage('Загрузка идёт дольше обычного — проверь интернет и попробуй ещё раз');
        } else {
          const msg = err instanceof Error ? err.message : 'Неизвестная ошибка';
          funnel.stage('upload_error');
          funnel.flush('failed');
          setHintMessage(`Ошибка загрузки: ${msg}`);
        }
        if (pendingPreviewRef.current === objectUrl) {
          URL.revokeObjectURL(objectUrl);
          pendingPreviewRef.current = null;
        }
        setPendingPreview((p) => (p?.index === currentIndex ? null : p));
      }
    },
    [checkpoint.slug, flowRun.slot_id, presign, requirements, upload],
  );

  const handleCta = useCallback(async () => {
    if (!isComplete) {
      const firstMissing = requirements.findIndex((_, index) => !uploaded[index]);
      pickSlot(firstMissing >= 0 ? firstMissing : 0);
      return;
    }
    if (isSubmitted || isSubmitting) return;

    const artefacts = requirements.map((req, index) => ({
      kind: req.kind,
      role: roleFor(req, index),
      artefact_path: uploaded[index].artefactPath,
    }));

    try {
      setHintMessage(null);
      await submitCheckpoint.mutateAsync({
        artefact_path: artefacts[0]?.artefact_path ?? null,
        artefacts,
      });
      setIsSubmitted(true);
      platform?.hapticImpact('medium');
    } catch (err: unknown) {
      setHintMessage(submitErrorText(err));
    }
  }, [
    isComplete,
    isSubmitted,
    isSubmitting,
    pickSlot,
    platform,
    requirements,
    submitCheckpoint,
    uploaded,
  ]);

  const ctaLabel = isSubmitted
    ? 'Готово'
    : isComplete
      ? 'Отправить'
      : `Загрузить ${Math.min(uploadedCount + 1, requirements.length)} из ${requirements.length}`;

  return (
    <Frame>
      <MiniHeader title={checkpoint.display_name ?? flowStepLabel(checkpoint.kind)} />
      <div className="px-4 pt-3 pb-2">
        <StepStrip
          current={checkpoint.position + 1}
          total={parentCount}
          sub={{ current: uploadedCount, total: requirements.length }}
          showCaption
        />
      </div>

      <div className="flex-1 overflow-y-auto px-4 pb-4 flex flex-col gap-4">
        <div style={{ paddingTop: 4 }}>
          <div style={{ font: T.title3, color: S.label, lineHeight: 1.2 }}>
            Загрузите фото и скриншоты
          </div>
          <div style={{ font: T.footnote, color: S.secondaryLabel, marginTop: 4 }}>
            {checkpoint.instructions_text ?? 'Добавьте все обязательные файлы.'}
          </div>
        </div>

        <InstructionCard p={12}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {requirements.map((req, index) => {
              const item = uploaded[index];
              const done = Boolean(item);
              const pending = pendingPreview?.index === index ? pendingPreview.url : null;
              const thumb = item?.thumbUrl ?? pending;
              const busy = pending !== null;
              return (
                <button
                  key={`${roleFor(req, index)}-${index}`}
                  type="button"
                  onClick={() => pickSlot(index)}
                  disabled={!canUpload || isSubmitted || isUploading || isSubmitting}
                  data-testid={`multi-upload-zone-${index}`}
                  style={{
                    width: '100%',
                    borderRadius: 16,
                    border: `2px solid ${done ? P.success : P.terracotta}`,
                    // Заливка зелёным только в пустом/загруженном-без-превью виде.
                    // Под самим превью фон тёмный, иначе objectFit:cover не нужен,
                    // а contain давал большие зелёные поля по бокам вертикальных
                    // скриншотов («зелёное огромное окно»).
                    background: thumb ? '#1C1B1A' : done ? SUCCESS_SOFT_BG : P.terraSoft,
                    padding: thumb ? 0 : '14px 12px',
                    minHeight: thumb ? 132 : 112,
                    maxHeight: thumb ? 132 : undefined,
                    cursor: canUpload && !isSubmitted ? 'pointer' : 'default',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'stretch',
                    overflow: 'hidden',
                    textAlign: 'left',
                    position: 'relative',
                  }}
                >
                  {thumb ? (
                    <>
                      <img
                        src={thumb}
                        alt={labelFor(req, index)}
                        style={{ width: '100%', height: 132, objectFit: 'cover', display: 'block' }}
                      />
                      {busy && (
                        <div
                          style={{
                            position: 'absolute',
                            inset: 0,
                            background: 'rgba(0,0,0,0.42)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 8,
                          }}
                          data-testid={`multi-upload-busy-${index}`}
                        >
                          <div
                            style={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              border: '3px solid rgba(255,255,255,0.4)',
                              borderTopColor: '#fff',
                              animation: 'spin 0.8s linear infinite',
                            }}
                          />
                          <div style={{ font: T.caption, fontWeight: 600, color: '#fff' }}>
                            Загружаем…
                          </div>
                        </div>
                      )}
                      {done && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            width: 26,
                            height: 26,
                            borderRadius: '50%',
                            background: P.success,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <Ic name="check" size={16} color="#fff" />
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <Ic name="camera" size={24} color={P.terracotta} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ font: T.subhead, fontWeight: 700, color: S.label }}>
                            {labelFor(req, index)}
                          </div>
                          <div
                            style={{
                              font: T.captionAlt,
                              color: S.secondaryLabel,
                              marginTop: 3,
                              lineHeight: 1.35,
                            }}
                          >
                            {descriptionFor(req)}
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </button>
              );
            })}
          </div>

          {isUploading && upload.progress !== null && (
            <div style={{ marginTop: 10 }}>
              <div
                style={{
                  height: 4,
                  borderRadius: 2,
                  background: P.line,
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    height: '100%',
                    width: `${Math.round((upload.progress ?? 0) * 100)}%`,
                    background: P.terracotta,
                    transition: 'width 0.2s linear',
                  }}
                />
              </div>
            </div>
          )}
        </InstructionCard>

        {!canUpload && <HintBar>Загрузка файлов недоступна на этой платформе.</HintBar>}
        {hintMessage && canUpload && <HintBar>{hintMessage}</HintBar>}
      </div>

      {canUpload && !isSubmitted && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleFileChange}
          data-testid="multi-image-input"
          aria-hidden="true"
        />
      )}

      <WizardCtaBar
        label={ctaLabel}
        onClick={handleCta}
        disabled={!canUpload || isSubmitted || isUploading || isSubmitting}
      />
    </Frame>
  );
}
